import type { ErrorResponse } from '@servicestack/client';
import type TSquareCustomerDetails from 'bz-core-controls/controls/payments/square/types/square-customer-details';
import CoreAuthStore from 'bz-stores/lib/auth-store';
import createContext from 'bz-utilities/hooks/context-v2';
import { clearToken, client, saveRefreshToken, setTokens } from 'connect/core/client';
import Session from 'connect/core/session';
import { action, computed, observable, runInAction } from 'mobx';
import type { CurrentUser } from 'tm3-core/api.dtos';
import {
  Authenticate,
  OnlineCurrentUserRequest,
  OnlineLogoutRequest,
  VerifySsoRegistrationRequest
} from 'tm3-core/api.dtos';
import cache from '../cache';

export const [AuthStoreContext, useAuthStore] = createContext<AuthStore>('AuthStore');

class AuthStore {
  @observable id = -1;
  @observable title = '';
  @observable forename = '';
  @observable surname = '';
  @observable dateOfBirth = '';
  @observable addressLine1 = '';
  @observable addressLine2 = '';
  @observable addressLine3 = '';
  @observable addressLine4 = '';
  @observable addressLine5 = '';
  @observable postcode = '';
  @observable homeTelephone = '';
  @observable workTelephone = '';
  @observable mobileTelephone = '';
  @observable email = '';
  @observable gender = '';
  @observable locationGuid = '';
  @observable practitionerGuid = '';
  @observable reference = '';
  @observable groupId: number | undefined = undefined;
  @observable paymentMethodId = '';
  @observable canBookAppointments = true;
  @observable lastAppointmentPractitionerGuid = '';
  @observable overridePayAtClinic = false;
  @observable insuranceNumber = '';

  @observable isAuthenticated: boolean = false;
  @observable loaded = false;
  @observable twoFactorSetup: boolean = false;
  @observable hasPassKey: boolean = false;
  @observable twoFactorAllowSms: boolean = false;
  @observable twoFactorChallenge: boolean = false;
  @observable ssoToken: string = '';

  @computed
  get customerFullName(): string {
    const { forename = '', surname = '', title = '' } = this;
    return `${title} ${forename} ${surname}`;
  }

  @computed
  get squareCustomerDetails(): TSquareCustomerDetails {
    const result: TSquareCustomerDetails = {
      addressLines: [
        this.addressLine1,
        this.addressLine2,
        this.addressLine3,
        this.addressLine4,
        this.addressLine5
      ],
      familyName: this.surname,
      givenName: this.forename,
      email: this.email,
      country: 'GB', //find this
      city: this.addressLine3,
      region: this.addressLine4,
      phone: this.mobileTelephone
    };
    return result;
  }

  async login(email: string, password: string) {
    try {
      const res = (await client.post(
        new Authenticate({ userName: email, password: password, provider: 'credentials' })
      )) as CurrentUser;

      this.onLogin(res);
    } catch (err) {
      return (err as ErrorResponse)?.responseStatus?.message;
    }
  }

  async loginWithSSO(email: string, provider: string) {
    try {
      const res = (await client.post(
        new VerifySsoRegistrationRequest({ email: email })
      )) as CurrentUser;

      this.onLogin(res);
    } catch (err) {
      return (err as ErrorResponse)?.responseStatus?.message;
    }
  }

  @action.bound
  public onLogin(res: CurrentUser) {
    const {
      customer,
      isAuthenticated,
      hasPassKey,
      twoFactorSetup,
      twoFactorAllowSms,
      twoFactorRequired,
      twoFactorAuthenticated,
      meta
    } = res;
    this.isAuthenticated = isAuthenticated;
    this.hasPassKey = hasPassKey;
    this.twoFactorSetup = twoFactorSetup;
    this.twoFactorAllowSms = twoFactorAllowSms;
    CoreAuthStore.user.twoFactorAllowSms = twoFactorAllowSms;
    if (twoFactorRequired && !twoFactorAuthenticated) {
      this.twoFactorChallenge = true;
      this.loaded = true;

      return '';
    } else if (isAuthenticated) {
      this.setUser(customer, meta);
    }
    this.isAuthenticated = isAuthenticated;
    setTokens(res.bearerToken, res.refreshToken);
    saveRefreshToken(res.refreshToken);
    this.loaded = true;
  }

  @action.bound
  async checkUser() {
    let isAuthenticated = false;

    try {
      const res = await client.get(new OnlineCurrentUserRequest());
      runInAction(() => {
        const {
          customer,
          isAuthenticated: serverIsAuthenticated,
          hasPassKey,
          twoFactorSetup,
          twoFactorAllowSms,
          meta
        } = res.result;
        isAuthenticated = !!serverIsAuthenticated;
        this.isAuthenticated = serverIsAuthenticated;
        this.hasPassKey = hasPassKey;
        this.twoFactorSetup = twoFactorSetup;
        this.twoFactorAllowSms = twoFactorAllowSms;

        if (serverIsAuthenticated) {
          this.setUser(customer, meta);
        } else {
          this.resetUser();
          clearToken();
        }
      });
    } catch (err) {
      runInAction(() => {
        this.resetUser();
        clearToken();
      });
      return false;
    } finally {
      runInAction(() => {
        this.loaded = true;
      });
    }

    return isAuthenticated;
  }

  @action.bound
  async logout() {
    try {
      await client.post(new OnlineLogoutRequest());
    } finally {
      Session.clear();
    }

    runInAction(() => {
      this.checkUser();
    });
  }

  getUser() {
    const { id, reference, title, forename, surname, mobileTelephone, email } = this;
    return { id, reference, title, forename, surname, mobileTelephone, email };
  }

  @action.bound
  setUser(client: any, meta: any) {
    CoreAuthStore.user.twoFactorAllowSms = this.twoFactorAllowSms;
    CoreAuthStore.user.mobileNumber = client.mobileTelephone;
    CoreAuthStore.user.name = `${client.forename} ${client.surname}`;
    CoreAuthStore.user.meta = meta;
    CoreAuthStore.stage1Authenticated = true;

    this.id = client.id ?? -1;
    this.title = client.title;
    this.forename = client.forename;
    this.surname = client.surname;
    this.dateOfBirth = client.dateOfBirth;
    this.addressLine1 = client.addressLine1;
    this.addressLine2 = client.addressLine2;
    this.addressLine3 = client.addressLine3;
    this.addressLine4 = client.addressLine4;
    this.addressLine5 = client.addressLine5;
    this.postcode = client.postCode;
    this.homeTelephone = client.homeTelephone;
    this.workTelephone = client.workTelephone;
    this.mobileTelephone = client.mobileTelephone;
    this.email = client.email;
    this.gender = client.gender;
    this.reference = client.reference;
    this.canBookAppointments = client.canBookAppointments;
    this.lastAppointmentPractitionerGuid = client.lastAppointmentPractitionerGuid || '-1';
    this.groupId = client.group?.id || undefined;
    this.locationGuid = client.locationGuid || '';
    this.practitionerGuid = client.practitionerGuid || '';
    this.insuranceNumber = client.insuranceNumber || '';
    this.paymentMethodId = client.paymentMethodId;
    this.overridePayAtClinic = client?.overridePayAtClinic;
    cache.searchStore.excludedPackages.replace(client.excludedPackages);
  }

  @action.bound
  resetUser() {
    CoreAuthStore.stage1Authenticated = false;
    CoreAuthStore.user.twoFactorAllowSms = false;
    CoreAuthStore.user.mobileNumber = '';
    CoreAuthStore.user.name = '';
    CoreAuthStore.user.meta = {};

    this.id = -1;
    this.title = '';
    this.forename = '';
    this.surname = '';
    this.dateOfBirth = '';
    this.addressLine1 = '';
    this.addressLine2 = '';
    this.addressLine3 = '';
    this.addressLine4 = '';
    this.addressLine5 = '';
    this.postcode = '';
    this.homeTelephone = '';
    this.workTelephone = '';
    this.mobileTelephone = '';
    this.email = '';
    this.gender = '';
    this.locationGuid = '';
    this.practitionerGuid = '';
    this.reference = '';
    this.groupId = undefined;
    this.paymentMethodId = '';
    this.canBookAppointments = true;
    this.lastAppointmentPractitionerGuid = '';
    this.overridePayAtClinic = false;
    this.insuranceNumber = '';
    this.isAuthenticated = false;
    this.twoFactorSetup = false;
    this.hasPassKey = false;
    this.twoFactorAllowSms = false;
    this.twoFactorChallenge = false;
    this.ssoToken = '';

    cache.searchStore.excludedPackages.clear();
  }
}

export default AuthStore;
